/** Poignées du mode édition du fuseau (docs/14 §2.3), en fonctions pures : où les dessiner, lesquelles montrer
 *  en permanence (allègement de l'affichage) et laquelle saisir sous le pointeur. Le calque (ManoeuvreLayers) et
 *  les gestes (useManoeuvreGestures) s'en servent ; la géométrie reste celle du modèle, seul l'affichage est
 *  allégé. Points en [lon, lat]. */

import { frameOf, helperDistance, planeOf, toLocal, type LonLat } from "./corridorFrame";
import { lineVertices, presentLines, type LineName, type Side, type UserCorridor } from "./corridorEdit";
import type { MeasureKey, Phase } from "../../api/types";
import type { Selection } from "../../state/ui";
import type { ManoeuvreHandle } from "./useManoeuvre";

/** Zone de saisie d'une poignée : cercle invisible de 12 px, 18 px au doigt. */
export const PICK_RADIUS_PX = 12;
export const PICK_RADIUS_COARSE_PX = 18;
/** Sommets mineurs : ils apparaissent en fondu quand le pointeur s'approche à moins de 60 px. */
export const MINOR_REVEAL_PX = 60;
/** En deçà, un sommet mineur est pleinement visible (rampe douce entre les deux). */
export const MINOR_FULL_PX = 36;
/** Sommet toujours affiché : angle sur sa limite au-delà de 15°, ou plus de 32 px de ses voisins affichés. */
export const MAJOR_ANGLE_DEG = 15;
export const MAJOR_SPACING_PX = 32;
/** Poignée de milieu seulement pour un segment de plus de 48 px à l'écran. */
export const MID_MIN_SEGMENT_PX = 48;

/** Mètres par pixel au zoom donné (tuiles de 512 px de Mapbox GL et MapLibre). */
export function metersPerPixel(zoom: number, lat: number): number {
  return (40_075_016.686 * Math.cos((lat * Math.PI) / 180)) / (512 * 2 ** zoom);
}

/** Rang de saisie d'une poignée en mode édition (docs/14 §2.3) : sommet −3, extrémité ou courbure de LIMA et
 *  extrémité d'une ligne de la ZO (docs/15 §4.3) −2, poignées de milieu −1 ; les unités (0), la LIMA entière (1) et
 *  la ligne de la ZO entière (1,5) viennent après. */
export function handleRank(handle: ManoeuvreHandle): number {
  switch (handle.kind) {
    case "vertex":
      return -3;
    case "limaEnd":
    case "limaBend":
    case "measureEnd":
      return -2;
    default:
      return -1;
  }
}

export interface HandlePoint {
  handle: ManoeuvreHandle;
  at: LonLat;
  /** Sommet affiché en permanence (sinon couche « mineure », visible près du pointeur). */
  major?: boolean;
}

/** Même poignée (identité, pas position). */
export function sameHandle(a: ManoeuvreHandle | null | undefined, b: ManoeuvreHandle | null | undefined): boolean {
  if (!a || !b || a.kind !== b.kind) return false;
  switch (a.kind) {
    case "vertex":
      return b.kind === "vertex" && a.side === b.side && a.index === b.index;
    case "mid":
      return b.kind === "mid" && a.index === b.index && (a.side ?? null) === (b.side ?? null);
    case "limaEnd":
      return b.kind === "limaEnd" && a.line === b.line && a.side === b.side;
    case "limaBend":
      return b.kind === "limaBend" && a.line === b.line && a.index === b.index;
    case "limaMid":
      return b.kind === "limaMid" && a.line === b.line && a.index === b.index;
    case "measureEnd":
      return b.kind === "measureEnd" && a.key === b.key && a.phase === b.phase && a.side === b.side;
  }
}

/** Clé stable d'une poignée (propriété des entités du calque). */
export function handleKey(handle: ManoeuvreHandle): string {
  switch (handle.kind) {
    case "vertex":
      return `v:${handle.side}:${handle.index}`;
    case "mid":
      return `m:${handle.side ?? "both"}:${handle.index}`;
    case "limaEnd":
      return `le:${handle.line}:${handle.side}`;
    case "limaBend":
      return `lb:${handle.line}:${handle.index}`;
    case "limaMid":
      return `lm:${handle.line}:${handle.index}`;
    case "measureEnd":
      return `me:${handle.key}:${handle.phase ?? "-"}:${handle.side}`;
  }
}

/** Angle (degrés, 0 à 180) du virage d'une polyligne au sommet `i` (0 aux extrémités). */
export function turnAngle(points: readonly LonLat[], i: number): number {
  if (i <= 0 || i >= points.length - 1) return 0;
  const plane = planeOf([points[i - 1]!, points[i]!, points[i + 1]!]);
  const [ax, ay] = toLocal(points[i - 1]!, plane);
  const [bx, by] = toLocal(points[i]!, plane);
  const [cx, cy] = toLocal(points[i + 1]!, plane);
  const u = [bx - ax, by - ay];
  const v = [cx - bx, cy - by];
  const nu = Math.hypot(u[0]!, u[1]!);
  const nv = Math.hypot(v[0]!, v[1]!);
  if (!(nu > 0) || !(nv > 0)) return 0;
  const cos = Math.min(1, Math.max(-1, (u[0]! * v[0]! + u[1]! * v[1]!) / (nu * nv)));
  return (Math.acos(cos) * 180) / Math.PI;
}

/** Sommets affichés en permanence sur une limite : extrémités, virages de plus de 15°, puis, de proche en proche,
 *  les sommets à plus de 32 px du dernier affiché et du prochain obligatoire. `mpp` : mètres par pixel. */
export function majorVertices(points: readonly LonLat[], mpp: number, forced: ReadonlySet<number> = new Set()): Set<number> {
  const n = points.length;
  const mandatory = new Set<number>();
  for (let i = 0; i < n; i++) {
    if (i === 0 || i === n - 1 || forced.has(i) || turnAngle(points, i) > MAJOR_ANGLE_DEG) mandatory.add(i);
  }
  const shown = new Set(mandatory);
  const px = (a: number, b: number) => helperDistance(points[a]!, points[b]!) / Math.max(mpp, 1e-9);
  let last = 0;
  for (let i = 1; i < n - 1; i++) {
    if (mandatory.has(i)) {
      last = i;
      continue;
    }
    let next = i + 1;
    while (next < n - 1 && !mandatory.has(next)) next++;
    if (px(i, last) > MAJOR_SPACING_PX && px(i, next) > MAJOR_SPACING_PX) {
      shown.add(i);
      last = i;
    }
  }
  return shown;
}

/** Milieu de deux points (degrés : à cette échelle, l'écart au milieu géodésique est négligeable). */
function middle(a: LonLat, b: LonLat): LonLat {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

export interface HandleSet {
  vertices: HandlePoint[];
  mids: HandlePoint[];
  limaEnds: HandlePoint[];
  limaBends: HandlePoint[];
  limaMids: HandlePoint[];
  /** Extrémités des lignes mobiles de la ZO (docs/15 §4.3). */
  measureEnds: HandlePoint[];
}

export interface HandleOptions {
  /** Mètres par pixel au zoom courant (allègement, filtre des milieux à 48 px). */
  mpp: number;
  /** LIMA sélectionnée : ses courbures et ses poignées de milieu s'affichent. */
  selectedLine?: LineName | null;
  /** Poignées toujours affichées comme majeures (survolée, saisie, sélectionnée). */
  emphasised?: readonly (ManoeuvreHandle | null | undefined)[];
  /** LIMA dessinées (calque visible). */
  lines?: boolean;
  /** Extrémités des lignes de la ZO du temps affiché (`measureView.measureEndPoints`), placées par le serveur ou
   *  par l'aperçu : elles ne dépendent pas de la géométrie du fuseau passée ici. */
  measureEnds?: readonly HandlePoint[];
}

/** Toutes les poignées d'un fuseau : sommets (majeurs ou mineurs), milieux de segments assez longs à l'écran,
 *  extrémités de LIMA, pour la LIMA sélectionnée ses courbures et les milieux de son tracé, et les extrémités des
 *  lignes de la ZO données. */
export function buildHandles(c: UserCorridor, options: HandleOptions): HandleSet {
  const { mpp } = options;
  const set: HandleSet = {
    vertices: [],
    mids: [],
    limaEnds: [],
    limaBends: [],
    limaMids: [],
    measureEnds: [...(options.measureEnds ?? [])],
  };
  const forcedOf = (side: Side) =>
    new Set(
      (options.emphasised ?? []).flatMap((h) => (h && h.kind === "vertex" && h.side === side ? [h.index] : [])),
    );
  for (const side of ["left", "right"] as const) {
    const rail = side === "left" ? c.left : c.right;
    const majors = majorVertices(rail, mpp, forcedOf(side));
    rail.forEach((at, index) =>
      set.vertices.push({ handle: { kind: "vertex", side, index }, at, major: majors.has(index) }),
    );
    for (let index = 0; index + 1 < rail.length; index++) {
      const a = rail[index]!;
      const b = rail[index + 1]!;
      if (helperDistance(a, b) / Math.max(mpp, 1e-9) > MID_MIN_SEGMENT_PX)
        set.mids.push({ handle: { kind: "mid", index, side }, at: middle(a, b) });
    }
  }
  if (options.lines === false || c.left.length < 2) return set;
  const frame = frameOf(c);
  for (const name of presentLines(c)) {
    const line = c.lines[name]!;
    set.limaEnds.push({ handle: { kind: "limaEnd", line: name, side: "left" }, at: frame.point(line.ends[0], -1) });
    set.limaEnds.push({ handle: { kind: "limaEnd", line: name, side: "right" }, at: frame.point(line.ends[1], 1) });
    if (options.selectedLine !== name) continue;
    line.bends.forEach(([s, t], index) =>
      set.limaBends.push({ handle: { kind: "limaBend", line: name, index }, at: frame.point(s, t) }),
    );
    const vertices = lineVertices(c, name, frame);
    for (let index = 0; index + 1 < vertices.length; index++) {
      const a = vertices[index]!;
      const b = vertices[index + 1]!;
      if (helperDistance(a, b) / Math.max(mpp, 1e-9) > MID_MIN_SEGMENT_PX)
        set.limaMids.push({ handle: { kind: "limaMid", line: name, index }, at: middle(a, b) });
    }
  }
  return set;
}

/** Toutes les poignées saisissables, dans un seul tableau. */
export function allHandles(set: HandleSet): HandlePoint[] {
  return [...set.vertices, ...set.limaEnds, ...set.limaBends, ...set.measureEnds, ...set.mids, ...set.limaMids];
}

export interface HandleHit {
  handle: ManoeuvreHandle;
  at: LonLat;
  /** Distance au pointeur, en pixels. */
  distance: number;
}

type Projector = (p: LonLat) => { x: number; y: number };

/** Deux poignées à moins de 4 px l'une de l'autre à l'écran sont confondues : le pointeur ne les départage plus
 *  qu'au demi-pixel, la sélection les départage (`pickHandle`). */
export const COINCIDENT_PX = 4;

/** Objet sélectionné dont les poignées l'emportent sur des poignées confondues : LIMA sélectionnée (ses extrémités
 *  et courbures) ou ligne de la ZO sélectionnée (sa fiche ouverte : ses extrémités). */
export interface HandleFocus {
  line?: LineName | null;
  measure?: { key: MeasureKey; phase: Phase | null } | null;
}

/** Objet sélectionné : LIMA sélectionnée du mode édition, ligne de la ZO dont la fiche est ouverte. */
export function focusOf(selectedLine: LineName | null, selection: Selection | null | undefined): HandleFocus {
  const measure = selection?.layer === "measure" ? { key: selection.key, phase: selection.phase ?? null } : null;
  return { line: selectedLine, measure };
}

/** Poignée de l'objet sélectionné. */
export function ownedBy(handle: ManoeuvreHandle, focus: HandleFocus | null | undefined): boolean {
  if (!focus) return false;
  if (handle.kind === "limaEnd" || handle.kind === "limaBend") return focus.line != null && handle.line === focus.line;
  if (handle.kind === "measureEnd") {
    const measure = focus.measure;
    return measure != null && handle.key === measure.key && (handle.phase ?? null) === (measure.phase ?? null);
  }
  return false;
}

/** Ordre de dessin des poignées (ManoeuvreLayers) : celles de l'objet sélectionné au-dessus des sommets, sommets
 *  au-dessus des autres extrémités (LIMA, lignes de la ZO). Une extrémité survolée ou saisie (`emphasised` : la
 *  poignée que rend `pickHandle` sous le pointeur, puis celle du geste) passe aussi au-dessus : sans sélection, la
 *  plus proche du pointeur est saisie même posée sous un sommet, et c'est elle que l'on voit au-dessus. */
export function drawOrder(
  handle: ManoeuvreHandle,
  focus: HandleFocus | null = null,
  emphasised: readonly (ManoeuvreHandle | null | undefined)[] = [],
): number {
  if (ownedBy(handle, focus)) return 2;
  if (handle.kind === "vertex") return 1;
  return emphasised.some((other) => sameHandle(handle, other)) ? 2 : 0;
}

/** Poignée saisie sous le pointeur (docs/14 §2.3) : parmi celles à moins de `radius` px, un sommet ou une
 *  extrémité / courbure de LIMA ou de ligne de la ZO l'emporte sur une poignée de milieu ; entre elles, la plus
 *  proche du pointeur ; à distance égale, le rang le plus bas. Poignées confondues (moins de `COINCIDENT_PX` à
 *  l'écran de la plus proche) : celle de l'objet sélectionné (`focus` : LIMA ou ligne de la ZO) l'emporte, la plus
 *  proche d'entre elles s'il y en a plusieurs ; sinon la plus proche reste retenue. Aucune poignée n'est jamais
 *  insaisissable : à plus de 4 px d'une autre, pointer dessus la donne ; confondue, sélectionner son objet (un clic
 *  sur la LIMA ou la ligne de la ZO) la donne, et désélectionner (clic sur la carte, Échap) rend la plus proche. */
export function pickHandle(
  handles: readonly HandlePoint[],
  point: { x: number; y: number },
  project: Projector,
  radius = PICK_RADIUS_PX,
  focus: HandleFocus | null = null,
): HandleHit | null {
  type Near = HandleHit & { q: { x: number; y: number }; key: [number, number, number] };
  const less = (a: Near["key"], b: Near["key"]) =>
    a[0] < b[0] || (a[0] === b[0] && (a[1] < b[1] || (a[1] === b[1] && a[2] < b[2])));
  let best: Near | null = null;
  const near: Near[] = [];
  for (const item of handles) {
    const q = project(item.at);
    const distance = Math.hypot(q.x - point.x, q.y - point.y);
    if (!(distance <= radius)) continue;
    const rank = handleRank(item.handle);
    const hit: Near = { handle: item.handle, at: item.at, distance, q, key: [rank <= -2 ? 0 : 1, distance, rank] };
    near.push(hit);
    if (!best || less(hit.key, best.key)) best = hit;
  }
  if (!best) return null;
  let chosen = best;
  if (best.key[0] === 0 && !ownedBy(best.handle, focus)) {
    // Poignée de l'objet sélectionné confondue avec la plus proche : elle l'emporte.
    let owned: Near | null = null;
    for (const hit of near) {
      if (hit.key[0] !== 0 || !ownedBy(hit.handle, focus)) continue;
      if (Math.hypot(hit.q.x - best.q.x, hit.q.y - best.q.y) > COINCIDENT_PX) continue;
      if (!owned || less(hit.key, owned.key)) owned = hit;
    }
    if (owned) chosen = owned;
  }
  return { handle: chosen.handle, at: chosen.at, distance: chosen.distance };
}

/** Opacité d'un sommet mineur selon la distance du pointeur (px) : 1 à moins de 36 px, 0 au-delà de 60 px,
 *  rampe douce entre les deux (le sommet apparaît en fondu à l'approche, jamais d'un coup). */
export function minorOpacity(distancePx: number | null): number {
  if (distancePx === null || !Number.isFinite(distancePx)) return 0;
  if (distancePx <= MINOR_FULL_PX) return 1;
  if (distancePx >= MINOR_REVEAL_PX) return 0;
  const x = (MINOR_REVEAL_PX - distancePx) / (MINOR_REVEAL_PX - MINOR_FULL_PX);
  return x * x * (3 - 2 * x);
}
